/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import yn from 'yn';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfoDrawer from '../../Layout/InfoDrawer';
import useSearchParams from '../../../hooks/useSearchParams';
import ClientsV2Details from './ClientV2Details';
import { clientDetailsEndpoints, detailsRequestHandler } from '../Clients/ClientDetails/clientDetailsAPI';
import {
  setClientAssig,
  setClientId,
  setClientPreferences,
  setClientPurchases,
  setClientTags,
  setMergeClientDetailsFromAPI,
  setName,
  setClearClient,
  setClearClientInfo,
} from '../../../reducers/client';
import { setLaunchFlagsForNav } from '../../../reducers/auth';
import { notificationError } from '../../../core/actions';
import { getRequest } from '../../../core/apiRequests';
import { useOnce } from '../../../hooks/useOnce';
import ClientList from './ClientList/index';
import { getClientById, getClientRelationships, getRelationshipTypes } from '../../../services/clients';
import {
  transformClientResponse,
  buildMixedRelationships,
  transformRelationshipsResponse,
} from '../../../services/clientTransformers';
import { getClientErrorMessage } from '../../../services/clientErrors';

const ClientsV2 = props => {
  const { getValue: getSearchParam, setValue } = useSearchParams();
  const params = getSearchParam('client');

  const [associateRefObj, setAssociateRefObj] = useState({});
  const [notesEnabled, setNotesEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toggleDrawer, setDrawerToggle] = useState(false);
  const [assignPopup, setAssignPopup] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const [clientFilterOpen, setClientFilterOpen] = useState(false);
  const [limitedAssociate, setLimitedAssociate] = useState(false);
  const [tableData, setTableData] = useState([]);
  const flags = useFlags();

  const checkAssociateVisibility = useCallback(async (clientId) => {
    const response = await getRequest({
      params: ['clients', clientId, 'followers'],
    });
    return !response.includes(props.auth.userId);
  }, [props.auth]);

  useEffect(() => {
    const clientChange = async () => {
      if (params) {
        if (yn(props.auth.merchantConfig.LIMIT_ASSOCIATE_VISIBILITY_ENABLED) &&
          props.auth.role === 'ASSOCIATE') {
          const limited = await checkAssociateVisibility(params);
          setLimitedAssociate(limited);
          if (limited) {
            return;
          }
        } else {
          // Reset limitedAssociate if visibility check is not needed
          setLimitedAssociate(false);
        }
        getClientData(params);
        setDrawerToggle(true);
      } else {
        // Reset limitedAssociate when no client is selected
        setLimitedAssociate(false);
        setDrawerToggle(false);
      }
    };
    clientChange();
  }, [params]);

  useEffect(() => {
    setLaunchFlagsForNav(flags);
  }, [flags?.projectEdison]);

  useEffect(() => {
    if (props.auth.merchantId && props.client.client_id) {
      (async () => {
        const reqObjClient = {
          params: ['clients', props.client.client_id, props.auth.merchantId, 'clientDetailsPreferences'],
        };
        const dataClient = await getRequest(reqObjClient);
        const reqObjMerchant = {
          params: ['clients', props.auth.merchantId, 0, 'clientPreferences'],
        };
        const data = await getRequest(reqObjMerchant);
        const formattedData = data[0]?.preferenceData?.map(item => {
          const prefClient = dataClient.find(i => i.config_id == item.config_id);
          if (prefClient) {
            return {
              ...item,
              value: prefClient.value,
              personalization_id: prefClient.personalization_id,
            };
          }
          return { ...item };
        });
        setClientPreferences(formattedData);
      })();
    }
  }, [props.auth.merchantId, props.client.client_id]);

  const getActivityList = async () => {
    const { merchantId } = props?.auth;
    setActivityList([]);
    const reqObj = {
      params: ['client', 'activityList', merchantId],
    };
    const data = await getRequest(reqObj);
    if (data) {
      setActivityList(data);
    } else {
      // notificationError('Error getting activity list');
    }
  };

  const getAssociateList = async () => {
    const { merchantId } = props?.auth;
    setActivityList([]);
    const reqObj = {
      params: ['associates', merchantId],
      query: {
        singleStore: 0
      }
    };
    const data = await getRequest(reqObj);
    if (Array.isArray(data) && data.length > 0) {
      let asctDataObj = {};
      data.forEach(asct => {
        if (asct.pos_ref_num) {
          asctDataObj[asct.pos_ref_num] = asct.fullName;
        }
      });
      setAssociateRefObj(asctDataObj);
    } else {
      console.warn('No associates found for mapping names from refnums');
    }
  };

  const { call: callGetActivityList } = useOnce(getActivityList);
  const { call: callGetAssociateList } = useOnce(getAssociateList);

  useEffect(() => {
    if (props.auth.merchantId) {
      callGetActivityList();
      callGetAssociateList();
    }
  }, [props.auth.merchantId]);

  const getClientPurchases = () => {
    const {
      auth: { merchantId },
      client: { client_id: clientId },
    } = props;
    if (merchantId && clientId) {
      (async () => {
        const reqObj = {
          params: ['client', merchantId, clientId, 'getAllClientPurchases'],
        };
        const data = await getRequest(reqObj);
        if (data) {
          setClientPurchases(data);
        } else {
          // notificationError('Error getting client purchases');
        }
      })();
    }
  };
  const { call: callGetClientPurchases } = useOnce(getClientPurchases);

  useEffect(() => {
    callGetClientPurchases();
  }, [props.auth.merchantId, props.client.client_id]);

  const clientDetailsAPI = async (clientId, merchantId) => {
    const endpointFieldMap = {
      'getClientCollections': 'collections',
      'getClientTags': 'details_tag_list',
      'getClientAutoMessages': 'autoMessages',
      'getClientRecentActivity': 'recentActivity',
      'getClientReminders': 'reminders',
      'getClientSalesOpps': 'salesOpps',
      'getClientPurchases': 'clientPurchases',
    };

    clientDetailsEndpoints.forEach(async eP => {
      const data = await detailsRequestHandler(clientId, merchantId, eP, eP === 'getClientRecentActivity' ? 9999 : 5);
      const fieldName = endpointFieldMap[eP];
      if (data && fieldName) {
        if (fieldName === 'salesOpps') {
          setMergeClientDetailsFromAPI({ ...data });
        } else {
          setMergeClientDetailsFromAPI({ [fieldName]: data[fieldName] || data });
        }
      }
    });
  };

  const selectClient = useCallback(id => {
    setValue('client', id);
    setValue('clientTab', '');
    setValue('collectionId', '');
    setValue('activityFilter', '');
  }, []);

  const getClientData = useCallback(
    async clientId => {
      setLoading(true);
      const {
        auth: { merchantId },
      } = props;
      if (merchantId && clientId) {
        clientDetailsAPI(clientId, merchantId);
        (async () => {
          try {
            // Use unified API for client details, relationships, and relationship types
            const [clientData, relationships, relTypesData] = await Promise.all([
              getClientById({ clientId, loadingStatusOff: true }),
              getClientRelationships({ clientId, loadingStatusOff: true }),
              getRelationshipTypes({ loadingStatusOff: true }),
            ]);

            if (clientData?.id) {
              const { id, name } = clientData;
              const allRelationships = relTypesData || [];

              // Use centralized transformers for data transformation
              const transformedClient = transformClientResponse(clientData);
              const transformedRelationships = transformRelationshipsResponse(relationships || []);
              const mixedRelationshipObj = buildMixedRelationships(id, transformedRelationships, allRelationships);

              const transformedData = {
                ...transformedClient,
                relationships: transformedRelationships,
                allRelationships,
              };

              setMergeClientDetailsFromAPI({ ...transformedData, mixedRelationships: mixedRelationshipObj });
              setClientId(id);
              setName(name);
              setClientAssig(transformedClient.associate_ref);
              setClientTags(clientData.count || 0);
            } else {
              notificationError('Client not found');
              selectClient(null);
              setClearClient();
            }
          } catch (error) {
            console.error('Error fetching client data:', error);
            notificationError(getClientErrorMessage(error, 'Client not found'));
            selectClient(null);
            setClearClient();
          }
        })();
        // Check to see if notes are enabled
        (async () => {
          const reqObj = {
            params: ['client', 'notes', merchantId],
          };
          const data = await getRequest(reqObj);
          if (data.length > 0 && data[0].config === 'true') {
            setNotesEnabled(true);
          } else {
            setNotesEnabled(false);
          }
        })();
        setLoading(false);
      } else {
        setClearClient();
        setClearClientInfo();
      }
    },
    [props.auth],
  );

  const panels = useMemo(() => ['7', '1', '2', '6', '8', '9'], []);

  const parentGridClassName = () => {
    let theClassName = 'three-column-parent-grid  rem-pad-width ';
    theClassName += 'three-column-parent-3-client';
    return theClassName;
  };

  const removeClientOfList = useCallback(
    id => {
      const newTableData = tableData.filter(client => client.id !== id);
      setTableData(newTableData);
    },
    [tableData],
  );

  return (
    <div
      id="ChatInboxId"
      className={`rem-pad-width${clientFilterOpen ? ' clients-filter-open' : ''}`}
      style={{ overflow: 'hidden', height: '100vh', scroll: 'none', width: '100%' }}
    >
      <div
        className={parentGridClassName()}
        style={{ height: `calc(100vh - 102px)` }}
      >
        <ClientList
          getClientData={selectClient}
          client={props.client}
          filters={props.filters}
          pagination={props.pagination}
          auth={props.auth}
          setClientFilterOpen={setClientFilterOpen}
          clientFilterOpen={clientFilterOpen}
          tableData={tableData}
          setTableData={setTableData}
        />
        {limitedAssociate ?
          <div key="startAConvoPlaceHolder" className="flex-col-center h-100-P">
            <FontAwesomeIcon
              icon="fa-regular fa-lock"
              color="#EBEBEB"
              size="9x"
              // className="color-bdbdbd"
              style={{ paddingBottom: '20px' }}
            />
            <span className="color-bdbdbd fs-14 w-500 p-btm-10">
              You do not have access to view this client. If you need access, please have your manager assign you to this
              client.
            </span>
          </div> :
          (<><ClientsV2Details
            setDrawerToggle={setDrawerToggle}
            toggleDrawer={toggleDrawer}
            setAssignPopup={setAssignPopup}
            chatIconColor="#479C83"
            type="full"
            client={props.client}
            auth={props.auth}
            associateRefObj={associateRefObj}
            paymentBanner={props.paymentBanner.active}
            activityList={activityList}
            getClientData={getClientData}
          />
            {toggleDrawer && (
              <InfoDrawer
                chatIconColor="#479C83"
                panels={panels}
                client={props.client}
                location="clientDetails"
                removeClientOfList={removeClientOfList}
                getClientData={getClientData}
              />
            )}
          </>
          )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  ...state,
});
const mapDispatchToProps = dispatch => ({});
export default connect(mapStateToProps, mapDispatchToProps)(ClientsV2);
